<template>
  <modal
    :show="show"
    modal-classes="modal-lg"
    @update:show="$emit('update:show', $event)"
  >
    <template v-slot:header>
      <h5 class="modal-title">Employee Breakdown</h5>
    </template>

    <div v-if="employee">
      <!-- Friendly Summary -->
      <div class="mb-3">
        <p class="lead mb-1">
          <strong>{{ employee.name }}</strong> is receiving
          <strong class="text-success">{{
            formatCurrency(employee.totalAmount)
          }}</strong>
          in this tip split.
        </p>
        <p class="text-muted text-sm mb-0">
          {{ summaryText }}
        </p>
        <!-- Warning for duplicate timesheets -->
        <div
          v-if="hasDuplicateTimesheets"
          class="alert alert-warning alert-sm py-2 px-3 mt-2 mb-0"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Potential issue:</strong> This employee received multiple
          shares from the same tip. This may indicate overlapping timesheets.
        </div>
      </div>

      <!-- Employee's Tip Breakdown - Grouped by Tip -->
      <div class="table-responsive" style="max-height: 400px; overflow-y: auto">
        <div
          v-for="group in groupedTips"
          :key="group.tipId"
          class="tip-group mb-3"
        >
          <!-- Tip Header -->
          <div class="tip-group-header">
            <div class="d-flex justify-content-between align-items-start mb-1">
              <div>
                <span class="h4 mb-0 text-dark"
                  >{{ group.tipAmountFormatted }}
                </span>
                <copy-text
                  :text="group.tipId"
                  label="Charge ID"
                  inline
                  icon-only
                  class="ml-2"
                  title="Copy Charge ID"
                />
                <span
                  v-if="hasNonRoundingDuplicates(group)"
                  class="badge badge-warning ml-2"
                >
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  {{ getNonRoundingShareCount(group) }} shares
                </span>
              </div>
            </div>
            <div class="text-sm text-muted">
              <i class="fas fa-map-marker-alt mr-1"></i>{{ group.locationName }}
              <span class="mx-2">•</span>
              <i class="far fa-clock mr-1"></i>{{ group.tipDateFormatted }}
              <span class="mx-2">•</span>
              <a
                href="#"
                class="text-primary"
                @click.prevent="$emit('view-tip', group.tipId)"
                >View Split by Tip <i class="fas fa-arrow-right ml-1"></i
              ></a>
            </div>
          </div>

          <!-- Shares Table -->
          <table class="table table-sm mb-0">
            <thead class="thead-light">
              <tr>
                <th>Share</th>
                <th>% of Tip</th>
                <th>Reason</th>
                <th>Shift</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(share, idx) in group.shares" :key="idx">
                <td>
                  <strong class="text-success">{{
                    share.shareAmountFormatted
                  }}</strong>
                </td>
                <td>
                  <span class="badge badge-light">{{
                    share.sharePercentage
                  }}</span>
                </td>
                <td>
                  <reason-badge :reason="share.reason" />
                </td>
                <td>
                  <small
                    >{{ share.shiftStartFormatted }} -
                    {{ share.shiftEndFormatted }}</small
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <button
        type="button"
        class="btn btn-secondary"
        @click="$emit('update:show', false)"
      >
        Close
      </button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import CopyText from "@/components/CopyText";
import ReasonBadge from "@/components/ReasonBadge";
import { formatCurrency } from "@/utils/formatters";

export default {
  name: "EmployeeBreakdownModal",
  components: {
    Modal,
    CopyText,
    ReasonBadge,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:show", "view-tip"],
  computed: {
    groupedTips() {
      if (!this.employee?.tips) return [];

      const tipGroups = new Map();

      this.employee.tips.forEach((tip) => {
        if (!tipGroups.has(tip.tipId)) {
          tipGroups.set(tip.tipId, {
            tipId: tip.tipId,
            tipAmount: tip.tipAmount,
            tipAmountFormatted: tip.tipAmountFormatted,
            tipDateFormatted: tip.tipDateFormatted,
            locationName: tip.locationName,
            shares: [],
            totalShare: 0,
          });
        }

        const group = tipGroups.get(tip.tipId);
        const shareAmount = tip.shareAmount || 0;
        const tipAmount = tip.tipAmount || 1;
        const percentage = ((shareAmount / tipAmount) * 100).toFixed(1);

        group.shares.push({
          ...tip,
          sharePercentage: `${percentage}%`,
        });
        group.totalShare += shareAmount;
      });

      return Array.from(tipGroups.values()).map((group) => ({
        ...group,
        totalPercentage: `${(
          (group.totalShare / (group.tipAmount || 1)) *
          100
        ).toFixed(1)}%`,
      }));
    },
    summaryText() {
      if (!this.employee) return "";

      const groups = this.groupedTips;
      const uniqueTips = groups.length;
      const totalShares = groups.reduce((sum, g) => sum + g.shares.length, 0);
      const locations = this.employee.locations?.length || 0;

      if (totalShares > uniqueTips) {
        return `Receiving ${totalShares} share(s) across ${uniqueTips} unique tip(s) from ${locations} location(s).`;
      }
      return `Breakdown of ${uniqueTips} tip(s) from ${locations} location(s).`;
    },
    hasDuplicateTimesheets() {
      return this.groupedTips.some((g) => this.hasNonRoundingDuplicates(g));
    },
  },
  methods: {
    formatCurrency,
    isRoundingReason(reason) {
      const r = reason?.toLowerCase() || "";
      return (
        r.includes("remainder") || r.includes("rounding") || r === "remainder"
      );
    },
    hasNonRoundingDuplicates(group) {
      const nonRoundingShares = group.shares.filter(
        (s) => !this.isRoundingReason(s.reason)
      );
      return nonRoundingShares.length > 1;
    },
    getNonRoundingShareCount(group) {
      return group.shares.filter((s) => !this.isRoundingReason(s.reason))
        .length;
    },
  },
};
</script>

<style lang="scss" scoped>
.tip-group {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  overflow: hidden;

  .tip-group-header {
    background: #f6f9fc;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e9ecef;

    code {
      font-size: 0.75rem;
      background: #e9ecef;
      padding: 0.15rem 0.35rem;
      border-radius: 3px;
    }
  }

  table {
    margin-bottom: 0;

    thead th {
      border-top: none;
    }
  }
}

.alert-sm {
  font-size: 0.875rem;
}

:deep(.modal-body) {
  padding-top: 0 !important;
}

:deep(.modal-header) {
  padding-bottom: 0.5rem;
}

:deep(.modal-body .lead) {
  margin-top: 0 !important;
}
</style>
